import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import {
  Mail, Users, Building2, Send, ArrowLeft, Search, CheckCircle2,
  AlertCircle, Clock, Loader2, X, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_OPTIONS = [
  { value: 'students',  label: 'All Students',  icon: Users,     accent: 'from-blue-500 to-cyan-500',     hint: 'Send to every registered student' },
  { value: 'companies', label: 'All Companies', icon: Building2, accent: 'from-emerald-500 to-teal-500',  hint: 'Send to every registered company' },
  { value: 'all',       label: 'Everyone',      icon: Mail,      accent: 'from-fuchsia-500 to-purple-600', hint: 'Students + Companies combined' },
  { value: 'selected',  label: 'Selected Users',icon: CheckCircle2, accent: 'from-amber-500 to-orange-500', hint: 'Cherry-pick recipients below' },
];

const AdminBroadcastEmailPage = () => {
  const navigate = useNavigate();

  const [audience, setAudience] = useState('students');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [counts, setCounts] = useState({ students: 0, companies: 0, all: 0 });

  const [students, setStudents] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [loadingPicker, setLoadingPicker] = useState(false);

  const [logs, setLogs] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) {
      toast.error('Please log in as admin first');
      navigate('/admin');
      return;
    }
    fetchCounts();
    fetchLogs();
  }, [token]);

  useEffect(() => {
    if (audience === 'selected') {
      fetchStudents('');
      fetchCompanies('');
    }
  }, [audience]);

  const fetchCounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/broadcast/audience-counts`, authConfig);
      setCounts(res.data || { students: 0, companies: 0, all: 0 });
    } catch (err) {
      // silent — UI still works
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/broadcast/logs`, authConfig);
      setLogs(res.data?.logs || []);
    } catch (err) {
      // silent
    }
  };

  const fetchStudents = async (q) => {
    setLoadingPicker(true);
    try {
      const res = await axios.get(`${API}/admin/broadcast/students`, {
        ...authConfig,
        params: { q: q || undefined, limit: 500 },
      });
      setStudents(res.data?.students || []);
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoadingPicker(false);
    }
  };

  const fetchCompanies = async (q) => {
    setLoadingPicker(true);
    try {
      const res = await axios.get(`${API}/admin/broadcast/companies`, {
        ...authConfig,
        params: { q: q || undefined, limit: 500 },
      });
      setCompanies(res.data?.companies || []);
    } catch (err) {
      toast.error('Failed to load companies');
    } finally {
      setLoadingPicker(false);
    }
  };

  const toggleId = (list, setter, id) => {
    setter(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const handleSend = async () => {
    if (subject.trim().length < 3) return toast.error('Subject must be at least 3 characters');
    if (message.trim().length < 10) return toast.error('Message must be at least 10 characters');
    if (audience === 'selected' && selectedStudentIds.length === 0 && selectedCompanyIds.length === 0) {
      return toast.error('Pick at least one student or company');
    }

    if (!window.confirm(`Send this email to ${audienceSummary()} now?`)) return;

    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast/send`, {
        subject: subject.trim(),
        message: message.trim(),
        audience,
        student_ids: selectedStudentIds,
        company_ids: selectedCompanyIds,
      }, authConfig);

      setLastResult(res.data);
      setShowResultModal(true);
      toast.success(`Sent ${res.data.sent_count} emails (${res.data.failed_count} failed)`);
      setSubject('');
      setMessage('');
      setSelectedStudentIds([]);
      setSelectedCompanyIds([]);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const audienceSummary = () => {
    if (audience === 'students')  return `all ${counts.students} students`;
    if (audience === 'companies') return `all ${counts.companies} companies`;
    if (audience === 'all')       return `all ${counts.all} users`;
    return `${selectedStudentIds.length} students + ${selectedCompanyIds.length} companies`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-broadcast-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Back to Admin</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center text-white">
              <Mail size={18} />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-slate-900 leading-tight">Email Broadcast</h1>
              <p className="text-[11px] sm:text-xs text-slate-500">Send announcements to students, companies or selected users</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 space-y-6">
          {/* Audience selection */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">1. Choose audience</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AUDIENCE_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = audience === opt.value;
                const sizeNote = (
                  opt.value === 'students'  ? counts.students :
                  opt.value === 'companies' ? counts.companies :
                  opt.value === 'all'       ? counts.all :
                  (selectedStudentIds.length + selectedCompanyIds.length)
                );
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAudience(opt.value)}
                    data-testid={`audience-${opt.value}`}
                    className={`relative text-left rounded-xl border-2 transition-all p-4 flex gap-3 items-start ${
                      active ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${opt.accent} flex items-center justify-center text-white shrink-0`}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">{opt.label}</p>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-900 text-white">
                          {sizeNote}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{opt.hint}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Selected pickers */}
          {audience === 'selected' && (
            <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5" data-testid="selected-pickers">
              <h2 className="text-sm font-semibold text-slate-900">2. Pick recipients</h2>

              <RecipientPicker
                title="Students"
                icon={Users}
                accent="text-blue-600"
                items={students}
                selectedIds={selectedStudentIds}
                onToggle={(id) => toggleId(selectedStudentIds, setSelectedStudentIds, id)}
                onSelectAll={() => setSelectedStudentIds(students.map(s => s.id))}
                onClear={() => setSelectedStudentIds([])}
                searchValue={studentSearch}
                onSearchChange={(v) => { setStudentSearch(v); fetchStudents(v); }}
                getLabel={(s) => s.full_name || s.username || 'Unnamed'}
                getSubLabel={(s) => `${s.email}${s.college ? ' · ' + s.college : ''}`}
                loading={loadingPicker}
                testid="student-picker"
              />

              <RecipientPicker
                title="Companies"
                icon={Building2}
                accent="text-emerald-600"
                items={companies}
                selectedIds={selectedCompanyIds}
                onToggle={(id) => toggleId(selectedCompanyIds, setSelectedCompanyIds, id)}
                onSelectAll={() => setSelectedCompanyIds(companies.map(c => c.id))}
                onClear={() => setSelectedCompanyIds([])}
                searchValue={companySearch}
                onSearchChange={(v) => { setCompanySearch(v); fetchCompanies(v); }}
                getLabel={(c) => c.company_name || 'Unnamed company'}
                getSubLabel={(c) => `${c.email}${c.contact_person ? ' · ' + c.contact_person : ''}`}
                loading={loadingPicker}
                testid="company-picker"
              />
            </section>
          )}

          {/* Message */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-4">
              {audience === 'selected' ? '3.' : '2.'} Compose your message
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. New hiring challenge open this week"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  data-testid="broadcast-subject-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write your announcement here. Line breaks are preserved."
                  rows={9}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-slate-900 font-mono"
                  data-testid="broadcast-message-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  {message.length} characters · sent as a branded EvalentX HTML email
                </p>
              </div>
            </div>
          </section>

          {/* Send */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500">Will deliver to <strong className="text-slate-900">{audienceSummary()}</strong></p>
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-fuchsia-600 to-purple-700 text-white font-semibold text-sm flex items-center gap-2 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="broadcast-send-btn"
            >
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {sending ? 'Sending…' : 'Send Broadcast'}
            </button>
          </div>
        </div>

        {/* Sidebar — recent logs */}
        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Recent broadcasts</h3>
              <button onClick={fetchLogs} className="text-xs text-slate-500 hover:text-slate-900">Refresh</button>
            </div>
            {logs.length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-500">
                No broadcasts yet. Your first campaign will appear here.
              </div>
            ) : (
              <ul className="space-y-2.5 max-h-[480px] overflow-y-auto pr-1" data-testid="broadcast-logs-list">
                {logs.map((log) => (
                  <li key={log.id} className="border border-slate-200 rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900 truncate">{log.subject}</p>
                      <StatusBadge status={log.status} sent={log.sent_count} failed={log.failed_count} />
                    </div>
                    <p className="text-slate-500 mt-1 truncate capitalize">
                      {log.audience || (log.recipient_type || '').replace('broadcast_', '')} · {log.total_recipients} recipients
                    </p>
                    <p className="text-slate-400 mt-1 text-[10px]">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-2xl p-5">
            <p className="text-xs uppercase tracking-widest text-slate-300">Reach overview</p>
            <p className="text-3xl font-bold mt-1">{counts.all}</p>
            <p className="text-xs text-slate-300 mt-0.5">total reachable accounts</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-[10px] uppercase text-slate-300">Students</p>
                <p className="font-bold text-lg">{counts.students}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-[10px] uppercase text-slate-300">Companies</p>
                <p className="font-bold text-lg">{counts.companies}</p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Result modal */}
      {showResultModal && lastResult && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowResultModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowResultModal(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700">
              <X size={18} />
            </button>
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 size={26} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Broadcast sent</h3>
            <p className="text-sm text-slate-500 mt-1">
              {lastResult.sent_count} of {lastResult.total_recipients} emails delivered ({lastResult.failed_count} failed)
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <Stat label="Recipients" value={lastResult.total_recipients} />
              <Stat label="Sent"       value={lastResult.sent_count}      tone="success" />
              <Stat label="Failed"     value={lastResult.failed_count}    tone="danger" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RecipientPicker = ({
  title, icon: Icon, accent, items, selectedIds, onToggle, onSelectAll, onClear,
  searchValue, onSearchChange, getLabel, getSubLabel, loading, testid,
}) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className={`flex items-center gap-2 text-sm font-semibold text-slate-900 ${accent}`}>
        <Icon size={16} />
        <span>{title}</span>
        <span className="text-xs font-medium text-slate-500">({selectedIds.length} selected)</span>
      </div>
      <div className="flex gap-2">
        <button onClick={onSelectAll} className="text-xs text-slate-700 hover:text-slate-900 underline">Select all</button>
        <button onClick={onClear} className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
      </div>
    </div>
    <div className="relative mb-2">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={`Search ${title.toLowerCase()} by name, email, college…`}
        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-slate-900"
      />
    </div>
    <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
      {loading ? (
        <div className="p-4 text-center text-xs text-slate-500"><Loader2 className="animate-spin mx-auto" size={16} /></div>
      ) : items.length === 0 ? (
        <p className="p-4 text-center text-xs text-slate-500">No {title.toLowerCase()} found</p>
      ) : items.map((item) => {
        const checked = selectedIds.includes(item.id);
        return (
          <label key={item.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs hover:bg-slate-50 ${checked ? 'bg-slate-50' : ''}`}>
            <input type="checkbox" checked={checked} onChange={() => onToggle(item.id)} className="rounded" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate">{getLabel(item)}</p>
              <p className="text-slate-500 truncate">{getSubLabel(item)}</p>
            </div>
          </label>
        );
      })}
    </div>
  </div>
);

const StatusBadge = ({ status, sent, failed }) => {
  const tone = status === 'completed'
    ? (failed > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')
    : 'bg-slate-100 text-slate-600';
  const icon = status === 'completed' ? (failed > 0 ? AlertCircle : CheckCircle2) : Clock;
  const Icon = icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${tone}`}>
      <Icon size={11} />
      {sent}/{sent + failed}
    </span>
  );
};

const Stat = ({ label, value, tone }) => {
  const cls = tone === 'success' ? 'text-emerald-600'
    : tone === 'danger' ? 'text-rose-600'
    : 'text-slate-900';
  return (
    <div className="rounded-lg border border-slate-200 p-2">
      <p className="text-[10px] uppercase text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${cls}`}>{value}</p>
    </div>
  );
};

export default AdminBroadcastEmailPage;
